import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { riskCalculationApi } from '../../services/riskCalculationApi';

export const riskCalculationKeys = {
  all: ['riskCalculations'] as const,
  lists: () => [...riskCalculationKeys.all, 'list'] as const,
  list: (filters?: { pageNumber?: number; pageSize?: number; search?: string }) =>
    [...riskCalculationKeys.lists(), filters] as const,
  byClinicalSession: (clinicalSessionId: string) =>
    [...riskCalculationKeys.all, 'byClinicalSession', clinicalSessionId] as const,
  byAssessmentExchange: (assessmentExchangeId: string) =>
    [...riskCalculationKeys.all, 'byAssessmentExchange', assessmentExchangeId] as const,
  details: () => [...riskCalculationKeys.all, 'detail'] as const,
  detail: (id: string) => [...riskCalculationKeys.details(), id] as const,
};

export function useGetRiskCalculation(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: riskCalculationKeys.detail(id),
    queryFn: async () => {
      const response = await riskCalculationApi.getById(id);
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch risk calculation'}`);
      }
      return response;
    },
    enabled: options?.enabled ?? !!id,
  });
}

export function useGetRiskCalculations() {
  return useQuery({
    queryKey: riskCalculationKeys.lists(),
    queryFn: async () => {
      const response = await riskCalculationApi.getList();
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch risk calculations'}`);
      }
      return response;
    },
  });
}

export function useGetRiskCalculationsByClinicalSessionId(
  clinicalSessionId: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: riskCalculationKeys.byClinicalSession(clinicalSessionId),
    queryFn: async () => {
      const response = await riskCalculationApi.getByClinicalSessionId(clinicalSessionId);
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch risk calculations'}`);
      }
      return response;
    },
    enabled: options?.enabled ?? !!clinicalSessionId,
  });
}

export function useGetRiskCalculationsByAssessmentExchangeId(
  assessmentExchangeId: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: riskCalculationKeys.byAssessmentExchange(assessmentExchangeId),
    queryFn: async () => {
      const response = await riskCalculationApi.getByAssessmentExchangeId(assessmentExchangeId);
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch risk calculations'}`);
      }
      return response;
    },
    enabled: options?.enabled ?? !!assessmentExchangeId,
  });
}

export function useGetRiskCalculationsPaged(
  pageNumber: number = 1,
  pageSize: number = 10,
  search?: string
) {
  return useQuery({
    queryKey: riskCalculationKeys.list({ pageNumber, pageSize, search }),
    queryFn: async () => {
      const response = await riskCalculationApi.getPagedList(pageNumber, pageSize, search);
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch risk calculations'}`);
      }
      return response;
    },
  });
}
